/**
 * ChecklistItemPhotoActions - Shared camera-column for a single
 * checklist row across both the work-order and inspection
 * surfaces. Branches purely off ChecklistItem.photoMode so each
 * row gets the right UI without callers having to duplicate the
 * branching logic:
 *
 *   - 'none'         → renders nothing (zero-width column collapses)
 *   - 'photo'        → one camera button; "Done" tag when a photo is
 *                      attached (server-persisted OR captured this
 *                      session).
 *   - 'before_after' → two camera buttons stacked vertically (BEFORE
 *                      on top, AFTER beneath) with independent
 *                      "Done" tags per slot.
 *
 * Inspections pass `optionalPhoto`: an inspector may want a picture of
 * any area, not just the ones the template asked for, so a 'none' item
 * still gets the single camera button there (never required — the
 * template's photoRequired gate is untouched). Work orders leave it off
 * and keep the template's word as final.
 *
 * Callers pass an onCapture(itemId, phase?) callback. Phase is
 * 'before' or 'after' for before_after rows and undefined for
 * single-photo rows so the parent's modal can scope the upload
 * accordingly.
 *
 * The "is the photo attached?" check unions server-persisted
 * item.photos (filtered by phoneticPhase) with the local
 * `capturedThisSession` snapshot so the UI flips to "Done"
 * instantly after upload, before the parent refetch lands.
 *
 * Because uploads now run in the background, the slot label also
 * reports where the photo is: "Sending" while it's in flight,
 * "Done" once the server has it, "Failed" if it didn't make it.
 * The 72px camera column has no room for the full UploadStatusChip,
 * so the label itself carries the state — the workflow's
 * PhotoUploadStatusBar is where the retry action lives.
 */
import React from 'react';
import type { ChecklistItem, CapturedPhotoPhases } from '../../../services/checklistService';
import type { PhotoUploadStatus } from '../../../hooks/usePhotoUploadQueue';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export type CapturePhase = 'before' | 'after';

/** The three slots a checklist item can hold a photo in. */
export type PhotoSlot = 'single' | CapturePhase;

/** Background-upload state per slot, for one item. */
export type SlotUploadStatuses = Partial<Record<PhotoSlot, PhotoUploadStatus>>;

export interface ChecklistItemPhotoActionsProps {
    /** The checklist item the actions belong to. */
    item: ChecklistItem;
    /**
     * Capture handler. Phase is undefined for single-photo items, and
     * 'before'/'after' for before_after items. The parent opens its
     * capture modal scoped to this (itemId, phase) pair.
     */
    onCapture: (itemId: string, phase?: CapturePhase) => void;
    /**
     * Local "captured this session" flags for this item. Lets the
     * "Done" badge flip optimistically while the parent refetches.
     */
    captured?: CapturedPhotoPhases;
    /**
     * Background-upload state per slot for this item, so the label can
     * say "Sending" / "Failed" instead of claiming "Done" for a photo
     * that is still on its way (or never arrived).
     */
    uploadStatus?: SlotUploadStatuses;
    /**
     * Offer a single optional camera button on items whose template set
     * photoMode='none', instead of rendering nothing. Used by inspections
     * (beta feedback: "Can we add the camera icon to all items?").
     */
    optionalPhoto?: boolean;
    /** Optional extra classes appended to the rendered root element. */
    className?: string;
}

/* ============================================
 * INTERNAL HELPERS
 * ============================================ */

/* Shared fixed width for the camera column. Applied to BOTH the
 * single-photo button and the before/after stack so the camera gutter
 * lines up to the same right edge regardless of which mode each row
 * uses. Sized to comfortably fit the widest content ("BEFORE"/"AFTER"
 * labels); the single-photo icon simply centers within the same box. */
const CAMERA_COLUMN_WIDTH = 'w-[72px]';

/** Camera SVG — shared between single + before/after buttons. */
const CameraIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z" />
    </svg>
);

/**
 * Reports whether a single slot on this item is filled — either from
 * the server-side photos array (matched by photoPhase, where null
 * matches the single-photo mode) or from the in-session captured
 * flags.
 */
function isSlotFilled(
    item: ChecklistItem,
    slot: PhotoSlot,
    captured?: CapturedPhotoPhases,
): boolean {
    if (slot === 'single') {
        return (item.photos?.length ?? 0) > 0 || !!captured?.single;
    }
    const persisted = (item.photos ?? []).some((p) => p.photoPhase === slot);
    return persisted || (slot === 'before' ? !!captured?.before : !!captured?.after);
}

/**
 * Resolves the short label under a slot's camera icon.
 *
 * Upload state wins over "filled" while a photo is in flight or has
 * failed — the optimistic flip to filled happens the instant the user
 * saves, and telling them "Done" before the bytes have landed is the
 * exact lie background uploading makes tempting.
 *
 * @returns The label text plus the Tailwind text color to render it in,
 *          or null when the slot has nothing to say.
 */
function slotStatusLabel(
    filled: boolean,
    status: PhotoUploadStatus | undefined,
    filledLabel: string,
): { text: string; tone: string } | null {
    if (status === 'uploading') return { text: 'Sending', tone: 'text-brand-primary' };
    if (status === 'waiting') return { text: 'Queued', tone: 'text-amber-600' };
    if (status === 'failed') return { text: 'Failed', tone: 'text-red-600' };
    if (filled) return { text: filledLabel, tone: '' };
    return null;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const ChecklistItemPhotoActions: React.FC<ChecklistItemPhotoActionsProps> = ({
    item,
    onCapture,
    captured,
    uploadStatus,
    optionalPhoto = false,
    className = '',
}) => {
    // The mode this row renders. A 'none' item is promoted to a single
    // optional camera when the caller asked for one (inspections).
    const mode = item.photoMode === 'none' && optionalPhoto ? 'photo' : item.photoMode;

    // mode='none' renders nothing — caller can also gate on this
    // upstream, but rendering a no-op here keeps the JSX in row
    // components consistent (always include the slot).
    if (mode === 'none') return null;

    // Single-photo mode: one camera column, "Done" tag once filled.
    if (mode === 'photo') {
        const filled = isSlotFilled(item, 'single', captured);
        const status = uploadStatus?.single;
        const label = slotStatusLabel(filled, status, 'Done');
        return (
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onCapture(item.id);
                }}
                className={[
                    'checklist-item-photo-actions',
                    filled ? 'checklist-item-photo-actions--filled' : '',
                    'flex-shrink-0 self-stretch flex flex-col items-center justify-center gap-2px px-8px',
                    CAMERA_COLUMN_WIDTH,
                    'border-l border-gray-200 active:bg-brand-primary/20',
                    filled ? 'text-green-700 hover:bg-green-50' : 'text-brand-primary hover:bg-brand-primary/10',
                    'min-h-[44px]',
                    className,
                ].join(' ')}
                data-ref="molecule-checklist-item-photo-actions"
                aria-label={filled ? 'Replace photo' : 'Take photo for this item'}
                title={filled ? 'Photo attached — tap to replace' : 'Take photo for this item'}
            >
                <CameraIcon className="w-32px h-32px" />
                {label && <span className={['text-9px font-bold uppercase tracking-wide', label.tone].join(' ')}>{label.text}</span>}
            </button>
        );
    }

    // Before/after mode: two stacked buttons in the camera column.
    // Stacked rather than side-by-side so the row keeps a consistent
    // height across mode=photo and mode=before_after items. Each
    // button is independently labeled + tracked.
    const beforeFilled = isSlotFilled(item, 'before', captured);
    const afterFilled = isSlotFilled(item, 'after', captured);
    const beforeLabel = slotStatusLabel(beforeFilled, uploadStatus?.before, 'Before ✓');
    const afterLabel = slotStatusLabel(afterFilled, uploadStatus?.after, 'After ✓');

    // Enforce capture order: the AFTER slot stays locked until a BEFORE
    // photo exists. An already-captured AFTER stays tappable (to replace)
    // even if BEFORE was somehow cleared, so a valid photo is never trapped.
    const afterDisabled = !beforeFilled && !afterFilled;

    const slotClasses = (filled: boolean) =>
        [
            'flex-1 flex flex-col items-center justify-center gap-2px px-8px py-4px',
            'active:bg-brand-primary/20',
            filled ? 'text-green-700 hover:bg-green-50' : 'text-brand-primary hover:bg-brand-primary/10',
            'min-h-[44px]',
        ].join(' ');

    // Column width matches the single-photo button (CAMERA_COLUMN_WIDTH)
    // so before/after rows share the same right-edge gutter; the two
    // stacked buttons are flex-1 and stretch to fill it.
    return (
        <div className={['checklist-item-photo-actions checklist-item-photo-actions--before-after flex-shrink-0 self-stretch flex flex-col border-l border-gray-200 divide-y divide-gray-200', CAMERA_COLUMN_WIDTH, className].join(' ')} data-ref="molecule-checklist-item-photo-actions">
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onCapture(item.id, 'before');
                }}
                className={`checklist-item-photo-actions__slot ${slotClasses(beforeFilled)}`}
                aria-label={beforeFilled ? 'Replace BEFORE photo' : 'Take BEFORE photo for this item'}
                title={beforeFilled ? 'BEFORE photo attached — tap to replace' : 'Take BEFORE photo'}
            >
                <CameraIcon className="w-22px h-22px" />
                <span className={['text-9px font-bold uppercase tracking-wide', beforeLabel?.tone ?? ''].join(' ')}>
                    {beforeLabel?.text ?? 'Before'}
                </span>
            </button>
            <button
                type="button"
                disabled={afterDisabled}
                onClick={(e) => {
                    e.stopPropagation();
                    if (afterDisabled) return; // guard against keyboard activation while locked
                    onCapture(item.id, 'after');
                }}
                className={[
                    'checklist-item-photo-actions__slot',
                    slotClasses(afterFilled),
                    afterDisabled ? 'opacity-40 cursor-not-allowed' : '',
                ].join(' ')}
                aria-label={
                    afterDisabled
                        ? 'Add the BEFORE photo first'
                        : afterFilled
                            ? 'Replace AFTER photo'
                            : 'Take AFTER photo for this item'
                }
                title={
                    afterDisabled
                        ? 'Take the BEFORE photo first'
                        : afterFilled
                            ? 'AFTER photo attached — tap to replace'
                            : 'Take AFTER photo'
                }
            >
                <CameraIcon className="w-22px h-22px" />
                <span className={['text-9px font-bold uppercase tracking-wide', afterLabel?.tone ?? ''].join(' ')}>
                    {afterLabel?.text ?? 'After'}
                </span>
            </button>
        </div>
    );
};

export default ChecklistItemPhotoActions;
